import type {DropResult} from '@hello-pangea/dnd';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {useDisclosure} from '@mantine/hooks';
import {useCallback, useMemo, useState} from 'react';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {DraggableList} from 'src/design/molecules/DraggableList';
import type {DraggableListItemContent} from 'src/design/molecules/DraggableListItem';
import {useGetFormDefinitionListQuery} from 'src/redux/queries/formDefinition';
import type { IFormFieldV2} from 'src/types/FormV2';
import {FormFieldTypeDisplayNameV2} from 'src/types/FormV2';
import {v4 as uuidv4} from 'uuid';
import {
  useGetFieldList,
  useGetFieldSettingsValid,
  useGetSelectedFieldId,
  useUpdateFieldList,
  useUpdateSelectedFieldId,
} from '../hooks/useFormBuilder';
import {useFormBuilderRegistry} from '../registry';

export const FieldList = () => {
  const fieldList = useGetFieldList();
  const setFieldList = useUpdateFieldList();
  const selectedFieldId = useGetSelectedFieldId();
  const updateSelectedFieldId = useUpdateSelectedFieldId();
  const fieldSettingsValid = useGetFieldSettingsValid();
  const {fieldTypes} = useFormBuilderRegistry();

  const [deleteConfirmOpened, deleteConfirmHandlers] = useDisclosure(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const supportsSubform = useMemo(
    () => fieldTypes.some(option => option.id === 'subform'),
    [fieldTypes]
  );

  const {data: formsPayload} = useGetFormDefinitionListQuery(
    {
      start: 0,
      length: 10000,
      active: true,
      includeSubforms: true,
      isRestrictedAccess: false,
    },
    {skip: !supportsSubform}
  );

  const subFormDefinitionNameById = useMemo(() => {
    const map = new Map<number, string>();
    (formsPayload?.data ?? []).forEach(definition => {
      if (definition.id !== undefined) {
        map.set(definition.id, definition.name);
      }
    });
    return map;
  }, [formsPayload]);

  // Create content items for DraggableList
  const draggableItems = useMemo(
    () =>
      fieldList.map(
        (field): DraggableListItemContent => ({
          id: field.invariantId!,
          primary: field.label.abbreviate(40),
          secondary:
            field.type === 'subform'
              ? subFormDefinitionNameById.get(
                  field.builderOptions?.subFormDefinitionId ?? -1
                ) ?? FormFieldTypeDisplayNameV2[field.type]
              : FormFieldTypeDisplayNameV2[field.type],
        })
      ),
    [fieldList, subFormDefinitionNameById]
  );

  // Calculate selected index from field ID for DraggableList
  const selectedIndex = useMemo(() => {
    if (!selectedFieldId) return 0;
    return fieldList.findIndex(field => field.invariantId === selectedFieldId);
  }, [fieldList, selectedFieldId]);

  const handleAddField = useCallback(() => {
    const name = `field_${uuidv4()}`;
    const newFieldId = uuidv4();

    const newField: IFormFieldV2 = {
      invariantId: newFieldId,
      name: name,
      label: 'New Field',
      type: 'text',
      required: true,
      builderOptions: {
        order: fieldList.length, // Add to the end of the list
      },
    };

    setFieldList([...fieldList, newField]);
    updateSelectedFieldId(newFieldId);
  }, [fieldList, setFieldList, updateSelectedFieldId]);

  const executeDelete = useCallback(
    (fieldId: string) => {
      // Find the field to be deleted
      const fieldToDelete = fieldList.find(
        field => field.invariantId === fieldId
      );
      if (!fieldToDelete) return;

      // Create new list without the deleted field and clean up visibility rules
      const items = fieldList
        .filter(field => field.invariantId !== fieldId)
        .map((field, index) => ({
          ...field,
          builderOptions: {
            ...field.builderOptions,
            order: index, // Update order to reflect new position
          },
          visibility: {
            ...field.visibility,
            rules: field.visibility?.rules?.filter(
              rule => rule.targetFieldInvariantId !== fieldId
            ),
          },
        }));

      setFieldList(items);

      // Select first available field if we deleted the selected one
      if (fieldId === selectedFieldId) {
        const newSelectedId = items.length > 0 ? items[0].invariantId! : null;
        updateSelectedFieldId(newSelectedId);
      }
    },
    [fieldList, selectedFieldId, setFieldList, updateSelectedFieldId]
  );

  const handleDeleteRequest = useCallback(
    (fieldId: string) => {
      setPendingDeleteId(fieldId);
      deleteConfirmHandlers.open();
    },
    [deleteConfirmHandlers]
  );

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId) {
      executeDelete(pendingDeleteId);
    }
    deleteConfirmHandlers.close();
    setPendingDeleteId(null);
  }, [pendingDeleteId, executeDelete, deleteConfirmHandlers]);

  const handleSelect = useCallback(
    (fieldId: string) => {
      updateSelectedFieldId(fieldId);
    },
    [updateSelectedFieldId]
  );

  const handleMoveUp = useCallback(
    (fieldId: string) => {
      const index = fieldList.findIndex(f => f.invariantId === fieldId);
      if (index <= 0) return;
      const items = Array.from(fieldList);
      const [removed] = items.splice(index, 1);
      items.splice(index - 1, 0, removed);
      const updatedItems = items.map((item, i) => ({
        ...item,
        builderOptions: {...item.builderOptions, order: i},
      }));
      setFieldList(updatedItems);
    },
    [fieldList, setFieldList]
  );

  const handleMoveDown = useCallback(
    (fieldId: string) => {
      const index = fieldList.findIndex(f => f.invariantId === fieldId);
      if (index < 0 || index >= fieldList.length - 1) return;
      const items = Array.from(fieldList);
      const [removed] = items.splice(index, 1);
      items.splice(index + 1, 0, removed);
      const updatedItems = items.map((item, i) => ({
        ...item,
        builderOptions: {...item.builderOptions, order: i},
      }));
      setFieldList(updatedItems);
    },
    [fieldList, setFieldList]
  );

  const handleDragEnd = useCallback(
    ({destination, source}: DropResult) => {
      if (!destination || destination.index === source.index) return;

      const draggedFieldId = fieldList[source.index].invariantId!;

      const items = Array.from(fieldList) as IFormFieldV2[];
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);

      // Update the order property for all items to reflect their new positions
      const updatedItems = items.map((item, index) => ({
        ...item,
        builderOptions: {
          ...item.builderOptions,
          order: index,
        },
      }));

      setFieldList(updatedItems);
      updateSelectedFieldId(draggedFieldId);
    },
    [fieldList, setFieldList, updateSelectedFieldId]
  );

  const pendingFieldLabel = pendingDeleteId
    ? fieldList.find(f => f.invariantId === pendingDeleteId)?.label
    : undefined;

  return (
    <>
      <Stack>
        <Group justify="center">
          <CreateButton
            onClick={handleAddField}
            size="sm"
            disabled={!fieldSettingsValid}>
            Add Field
          </CreateButton>
        </Group>
        <DraggableList
          items={draggableItems}
          onDragEnd={handleDragEnd}
          selectedIndex={selectedIndex}
          disabled={!fieldSettingsValid}
          onSelect={handleSelect}
          onDelete={handleDeleteRequest}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
        />
        {fieldList.length > 5 && (
          <Group justify="center">
            <CreateButton
              onClick={handleAddField}
              size="sm"
              disabled={!fieldSettingsValid}>
              Add Field
            </CreateButton>
          </Group>
        )}
      </Stack>

      <ConfirmDialog
        opened={deleteConfirmOpened}
        onClose={() => {
          deleteConfirmHandlers.close();
          setPendingDeleteId(null);
        }}
        onConfirm={handleConfirmDelete}
        confirmationButtonColor="red"
        title="Delete Field"
        body={`Are you sure you want to delete "${
          pendingFieldLabel ?? 'this field'
        }"? This will also remove any visibility rules that reference it.`}
        buttonTextYes="Delete"
      />
    </>
  );
};
